/**
 * What can be switched on a device, and how far a command has got. No React here so it's
 * easy to test. The rule throughout: nothing shows as done until the device confirms it.
 */
import type { CommandRecord, Device, PortConfig, Reading } from "./api.ts";
import { isFmsBoard, portKey, portLabel } from "./format.ts";
import { can as canRole, type Permission } from "../../../packages/core/src/permissions.ts";
import { PORT_COUNTS } from "../../../packages/fms-protocol/src/types.ts";
import { milesightOutputs } from "../../../packages/drivers/src/milesight-commands.ts";

export const can = (role: string, permission: Permission) => canRole({ role } as Parameters<typeof canRole>[0], permission);

export interface Switchable {
  /** FMS outputs by kind and channel; "port" for a Milesight output named by its channel (do_1, gpio_out_2, valve_1). */
  kind: "do" | "ro" | "port";
  channel: number;
  key: string;
  label: string;
  /** A UC51x valve: can be opened for a set time. */
  valve?: boolean;
  /** A QOTO valve: opens to a percentage; `opening` is what it last reported. */
  percent?: boolean;
  opening?: number;
  /** Last state the device reported, if any. */
  on?: boolean;
  at?: number;
}

export interface AnalogOut {
  channel: number;
  key: string;
  label: string;
  port?: PortConfig;
  value?: number;
  unit?: string;
  at?: number;
}

/**
 * Outputs on an FMS board that the app offers to switch: every output set up and enabled,
 * plus any the board reports even if not set up yet. MultiPro do5 is the enclosure fan, so
 * it only shows when someone has deliberately set it up.
 */
export function switchables(device: Device, readings: Reading[]): Switchable[] {
  const latest = new Map(readings.map((r) => [r.port, r]));
  if (!isFmsBoard(device.model)) {
    // Milesight controllers: the outputs the model has, unless switched off in set-up.
    const disabled = new Set(device.ports.filter((p) => !p.enabled).map(portKey));
    return milesightOutputs(device.model)
      .filter((o) => !disabled.has(o.port))
      .map((o) => {
        const r = latest.get(o.port);
        return {
          kind: "port" as const,
          channel: o.channel,
          key: o.port,
          label: portLabel(device, o.port),
          ...(o.kind === "valve" ? { valve: true } : {}),
          ...(o.kind === "qoto" ? { percent: true, ...(r ? { opening: r.value } : {}) } : {}),
          ...(r ? { on: r.value !== 0, at: r.at } : {}),
        };
      });
  }
  const keys = new Set<string>();
  for (const p of device.ports) if ((p.kind === "do" || p.kind === "ro") && p.enabled) keys.add(portKey(p));
  for (const r of readings) if (/^(do|ro)\d+$/.test(r.port) && !(device.model === "multipro" && r.port === "do5")) keys.add(r.port);
  const disabled = new Set(device.ports.filter((p) => !p.enabled).map(portKey));
  return [...keys]
    .filter((k) => !disabled.has(k))
    .map((key) => {
      const kind = key.slice(0, 2) as "do" | "ro";
      const r = latest.get(key);
      return {
        kind,
        channel: Number(key.slice(2)),
        key,
        label: portLabel(device, key),
        ...(r ? { on: r.value !== 0, at: r.at } : {}),
      };
    })
    .sort((a, b) => (a.kind === b.kind ? a.channel - b.channel : a.kind === "ro" ? -1 : 1));
}

export function analogOutputs(device: Device, readings: Reading[]): AnalogOut[] {
  if (!isFmsBoard(device.model)) return [];
  const latest = new Map(readings.map((r) => [r.port, r]));
  return device.ports
    .filter((p) => p.kind === "ao" && p.enabled)
    .map((p) => {
      const key = portKey(p);
      const r = latest.get(key);
      const unit = p.scale?.unit ?? r?.unit;
      return {
        channel: p.channel,
        key,
        label: portLabel(device, key),
        port: p,
        ...(r ? { value: r.value, at: r.at } : {}),
        ...(unit ? { unit } : {}),
      };
    })
    .sort((a, b) => a.channel - b.channel);
}

/** All ports a board has, for the set-up screen (MultiPro: ai1-8, di1-6, do1-5, ro1-4, ao1-8). */
export function boardPorts(model: string): Array<{ kind: "ai" | "di" | "do" | "ro" | "ao"; channel: number }> {
  if (model !== "multipro" && model !== "mini") return [];
  const counts = PORT_COUNTS[model];
  const out: Array<{ kind: "ai" | "di" | "do" | "ro" | "ao"; channel: number }> = [];
  for (const kind of ["ai", "di", "ro", "do", "ao"] as const) for (let c = 1; c <= counts[kind]; c++) out.push({ kind, channel: c });
  return out;
}

export const FINAL: ReadonlySet<CommandRecord["status"]> = new Set(["confirmed", "unconfirmed", "failed", "cancelled"]);

export interface Stage {
  label: string;
  state: "done" | "now" | "todo" | "fail";
}

/** Queued → Sent → Confirmed, as the prototype showed it, from the real command record. */
export function lifecycle(c: Pick<CommandRecord, "status" | "attempts" | "failReason">): Stage[] {
  const st = c.status;
  if (st === "failed" || st === "cancelled") {
    const sent = c.attempts > 0;
    return [
      { label: "Queued", state: "done" },
      { label: sent ? `Sent${c.attempts > 1 ? ` ×${c.attempts}` : ""}` : "Not sent", state: sent ? "done" : "fail" },
      { label: st === "cancelled" ? "Cancelled" : sent ? "No reply" : "Failed", state: "fail" },
    ];
  }
  if (st === "unconfirmed") {
    return [
      { label: "Queued", state: "done" },
      { label: "Sent", state: "done" },
      { label: "Can't be confirmed", state: "todo" },
    ];
  }
  return [
    { label: "Queued", state: st === "queued" ? "now" : "done" },
    { label: `Sent${c.attempts > 1 ? ` ×${c.attempts}` : ""}`, state: st === "sent" ? "now" : st === "queued" ? "todo" : "done" },
    { label: "Confirmed", state: st === "confirmed" ? "done" : "todo" },
  ];
}
